import { PLAYER_STATE } from '@components/player/enums';
import Chromecast, { EVENT as CHROMECAST_EVENT } from '@videotools/js/util/Chromecast';

const chromecast = (window.isStorybook || window.isSmartTv) ? null : new Chromecast();
const defaultVolumeLevel = 50;
const getUserPreferedVolumeLevel = () => {
	try {
		return parseInt(localStorage.getItem('userPreferedVolumeLevel'));
	} catch {
		// Some mobile browsers block localStorage in restricted contexts; fall back to the default volume.
		return NaN;
	}
};
const userPreferedVolumeLevel = getUserPreferedVolumeLevel();
const initialVolumeLevel = !isNaN(userPreferedVolumeLevel) && typeof userPreferedVolumeLevel === 'number'
	? userPreferedVolumeLevel
	: defaultVolumeLevel;

export const initialState = () => {
	return {
		playStatus:           PLAYER_STATE.INITIAL,
		isContentInfoEnabled: true,
		isFakeDVR:            false,
		playerQualityLevels:  [],
		playSpeed:            1,
		airPlay:              {
			available: false,
			videoEl:   null,
		},
		audioTracks:                 [],
		currentAudioTrackId:         null,
		chromecast,
		isChromecastAvailable:       false,
		isPictureInPictureAvailable: false,
		volumeLevel:                 initialVolumeLevel,
		userPreferedVolumeLevel:     initialVolumeLevel,
		isMuted:                     false,
		wasMutedManually:            false,
		googleImaIsActive:           false,
		isAutoplayMuted:             false,
	};
};

export default {
	namespaced: true,
	state:      initialState,
	mutations:  {
		setPlayStatus(state, status) {
			state.playStatus = status;
		},
		setContentInfoEnabled(state, status) {
			state.isContentInfoEnabled = Boolean(status);
		},
		setFakeDVR(state, value) {
			state.isFakeDVR = value;
		},
		setPlayerQualityLevels(state, levels) {
			state.playerQualityLevels = levels.map((level, id) => ({ ...level, id }));
		},
		setPlaySpeed(state, value) {
			state.playSpeed = value;
		},
		setAirPlay(state, payload) {
			state.airPlay = payload;
		},
		setAudioTracks(state, value) {
			state.audioTracks = value;
		},
		setCurrentAudioTrackId(state, value) {
			state.currentAudioTrackId = value;
		},
		setIsChromecastAvailable(state, value) {
			state.isChromecastAvailable = value;
		},
		setIsPictureInPictureAvailable(state, value) {
			state.isPictureInPictureAvailable = value;
		},
		setVolumeLevel(state, value) {
			state.volumeLevel = value;
		},
		setUserPreferedVolumeLevel(state, value) {
			state.userPreferedVolumeLevel = value;
		},
		setMute(state, value) {
			state.isMuted = value;
		},
		setWasMutedManually(state, value) {
			state.wasMutedManually = value;
		},
		setGoogleImaActive(state, value) {
			state.googleImaIsActive = value;
		},
		setAutoplayMuted(state, value) {
			state.isAutoplayMuted = value;
		},
	},
	actions: {
		init({ commit }) {
			if (window.isStorybook || window.isSmartTv) return;
			chromecast.addEventListener(CHROMECAST_EVENT.INIT, () => commit('setIsChromecastAvailable', true));
		},
		setPlayStatus({ commit }, status) {
			commit('setPlayStatus', status);
		},
		setPlaying({ dispatch }) {
			dispatch('setPlayStatus', PLAYER_STATE.PLAYING);
		},
		setPaused({ dispatch }) {
			dispatch('setPlayStatus', PLAYER_STATE.PAUSED);
		},
		setFakeDVR({ commit }, value) {
			commit('setFakeDVR', value);
		},
		setQualityLevels({ commit }, levels = []) {
			commit('setPlayerQualityLevels', levels);
		},
		setPlaySpeed({ commit }, value) {
			commit('setPlaySpeed', value);
		},
		setAirPlay({ commit }, payload) {
			commit('setAirPlay', payload);
		},
		setAudioTracks({ commit }, value) {
			commit('setAudioTracks', value);
		},
		setCurrentAudioTrackId({ commit }, value) {
			commit('setCurrentAudioTrackId', value);
		},
		setIsPictureInPictureAvailable({ commit }, value) {
			commit('setIsPictureInPictureAvailable', value);
		},
		setVolumeLevel({ commit, dispatch }, value) {
			const clampedValue = Math.max(0, Math.min(value, 100));
			commit('setVolumeLevel', clampedValue);

			dispatch('setUserPreferedVolumeLevel', clampedValue);
		},
		setUserPreferedVolumeLevel({ commit }, value) {
			localStorage.setItem('userPreferedVolumeLevel', value);
			commit('setUserPreferedVolumeLevel', value);
		},
		setMute({ commit }, value) {
			commit('setMute', value);
		},
		setWasMutedManually({ commit }, value) {
			commit('setWasMutedManually', value);
		},
		setGoogleImaActive({ commit }, value) {
			commit('setGoogleImaActive', value);
		},
		setAutoplayMuted({ commit }, value) {
			commit('setAutoplayMuted', value);
		},
	},
	getters: {
		playStatus:            (state) => state.playStatus,
		isPlaying:             (state) => state.playStatus === PLAYER_STATE.PLAYING,
		isPlayingOrPaused:     (state) => [PLAYER_STATE.PLAYING, PLAYER_STATE.PAUSED].includes(state.playStatus),
		isPaused:              (state) => [PLAYER_STATE.INITIAL, PLAYER_STATE.IDLE, PLAYER_STATE.PAUSED].includes(state.playStatus),
		isFakeDVR:             (state) => state.isFakeDVR,
		isAdMode:              (state) => [PLAYER_STATE.AD_IDLE, PLAYER_STATE.AD_PLAYING, PLAYER_STATE.AD_PAUSED].includes(state.playStatus),
		isAdModePaused:        (state) => state.playStatus === PLAYER_STATE.AD_PAUSED,
		isAdModeIdle:          (state) => state.playStatus === PLAYER_STATE.AD_IDLE,
		playerQualityLevels:   (state) => state.playerQualityLevels,
		playSpeed:             (state) => state.playSpeed,
		airPlay:               (state) => state.airPlay,
		isAirPlayAvailable:    (state) => state.airPlay.available,
		audioTracks:           (state) => state.audioTracks,
		currentAudioTrackId:   (state) => state.currentAudioTrackId,
		isChromecastAvailable: (state, _getters, _rootState, rootGetters) => {
			return state.isChromecastAvailable && !rootGetters['features/forceDisableChromeCast'];
		},
		isPictureInPictureAvailable: (state) => state.isPictureInPictureAvailable,
		chromecast:                  (state) => state.chromecast,
		volumeLevel:                 (state) => state.volumeLevel,
		userPreferedVolumeLevel:     (state) => state.userPreferedVolumeLevel,
		isMuted:                     (state) => state.isMuted,
		wasMutedManually:            (state) => state.wasMutedManually,
		googleImaIsActive:           (state) => state.googleImaIsActive,
		isAutoplayMuted:             (state) => state.isAutoplayMuted,
	},
};
